import { apiBaseUrl, portalBaseUrl, type EnvironmentId } from '@/config/environments';

import type { ApiErrorBody } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: ApiErrorBody;

  constructor(status: number, message: string, code?: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type RequestContext = {
  env: EnvironmentId;
  /** Partner key — sent as the `api-key` header (tech-design §5 Auth). */
  apiKey: string;
};

type HttpOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Low-level call against the selected environment's `/v2` base URL. Throws {@link ApiError}
 * on non-2xx. Auth header is supplied by the caller (partner `api-key` or admin `x-admin-api-key`).
 */
export async function http<T>(
  env: EnvironmentId,
  path: string,
  { method = 'GET', body, headers = {}, signal }: HttpOptions = {}
): Promise<T> {
  const response = await fetch(`${apiBaseUrl(env)}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const errBody = parsed as ApiErrorBody | undefined;
    const message = errBody?.error?.message ?? `Request to ${path} failed with ${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message, errBody?.error?.code, errBody);
  }

  return parsed as T;
}

/** Low-level call against the portal base URL (`/portal/*`, no `/v2` prefix). */
export async function portalHttp<T>(env: EnvironmentId, path: string, opts: HttpOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = opts;
  const response = await fetch(`${portalBaseUrl(env)}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const errBody = parsed as ApiErrorBody | undefined;
    const message = errBody?.error?.message ?? `Request to ${path} failed with ${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message, errBody?.error?.code, errBody);
  }

  return parsed as T;
}

/** Partner-key authenticated request (`api-key` header). Extra headers (e.g. `Melio-Entity-Id`) merge in. */
export async function request<T>({ env, apiKey }: RequestContext, path: string, opts: HttpOptions = {}): Promise<T> {
  if (!apiKey) {
    throw new ApiError(401, 'Missing API key. Enter your partner key to call the API.');
  }
  return http<T>(env, path, { ...opts, headers: { 'api-key': apiKey, ...opts.headers } });
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
